import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import DeploymentOptions from '../components/DeploymentOptions';

export function StoryWeaver() {
  const [contentInput, setContentInput] = useState('');
  const [format, setFormat] = useState('presentation');
  const [tone, setTone] = useState('professional');
  const [audience, setAudience] = useState('stakeholders');
  const [genre, setGenre] = useState('business');
  const [style, setStyle] = useState('formal');
  const [contentLength, setContentLength] = useState('medium');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  const formatOptions = [
    { value: 'presentation', label: 'Executive Presentation' },
    { value: 'report', label: 'Detailed Report' },
    { value: 'email', label: 'Professional Email' },
    { value: 'newsletter', label: 'Company Newsletter' },
    { value: 'proposal', label: 'Business Proposal' },
    { value: 'summary', label: 'Executive Summary' },
    { value: 'minutes', label: 'Meeting Minutes' }
  ];

  const toneOptions = [
    { value: 'professional', label: 'Professional & Authoritative' },
    { value: 'analytical', label: 'Analytical & Data-Driven' },
    { value: 'persuasive', label: 'Persuasive & Strategic' },
    { value: 'consultative', label: 'Consultative & Advisory' },
    { value: 'engaging', label: 'Engaging & Informative' },
    { value: 'concise', label: 'Concise & Clear' }
  ];

  const audienceOptions = [
    { value: 'stakeholders', label: 'Key Stakeholders' },
    { value: 'executives', label: 'Executive Leadership' },
    { value: 'team-members', label: 'Team Members' },
    { value: 'clients', label: 'Client Partners' },
    { value: 'all-staff', label: 'All Staff' }
  ];

  const genreOptions = [
    { value: 'business', label: 'Business' },
    { value: 'technical', label: 'Technical' },
    { value: 'academic', label: 'Academic' },
    { value: 'creative', label: 'Creative' }
  ];

  const styleOptions = [
    { value: 'formal', label: 'Formal' },
    { value: 'conversational', label: 'Conversational' },
    { value: 'analytical', label: 'Analytical' },
    { value: 'narrative', label: 'Narrative' },
    { value: 'newsletter', label: 'Newsletter Style' }
  ];

  const lengthOptions = [
    { value: 'short', label: 'Short (< 500 words)' },
    { value: 'medium', label: 'Medium (500-1000 words)' },
    { value: 'long', label: 'Long (> 1000 words)' }
  ];

  const quickStartGuides = [
    {
      title: "Content Strategy",
      description: "Define your message format and target audience to create impactful business communications.",
      keywords: ["Format", "Audience", "Messaging"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "AI Enhancement",
      description: "Leverage AI to transform your ideas into polished, professional communications.",
      keywords: ["Refinement", "Structure", "Clarity"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Review & Finalize",
      description: "Review generated content and ensure alignment with business objectives.",
      keywords: ["Review", "Objectives", "Delivery"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setContentInput(transcribedText);
  };

  const generateContent = async () => {
    if (!contentInput.trim()) {
      toast.error('Please provide some content input');
      return;
    }

    setIsGenerating(true);

    try {
      const prompt = `Create a compelling ${format === 'newsletter' ? 'newsletter' : 'story'} with the following details:
Input: ${contentInput}
Genre: ${genre}
Style: ${style}
Length: ${contentLength}
Format: ${format}
Tone: ${tone}
Audience: ${audience}

${format === 'newsletter' ? `Please structure the newsletter with:
- Engaging headline
- Brief introduction/editor's note
- Main stories or updates
- Key highlights or announcements
- Call-to-action or engagement section
- Relevant images or media placement suggestions
- Consistent branding elements` : `Please structure the content with:
- Clear introduction
- Well-developed main points
- Engaging narrative flow
- Appropriate conclusion
- Relevant examples or anecdotes
- Language suitable for the specified audience`}

Format the response as a cohesive piece of content that matches the selected style and tone.`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        const generatedText = response.content || response.message;
        if (generatedText) {
          setGeneratedContent(generatedText);
          // Add the initial generation as a system message
          setMessages([{
            content: generatedText,
            sender: 'ai',
            timestamp: new Date().toISOString(),
            type: 'initial'
          }]);
          toast.success('Content generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error.message || 'Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || !generatedContent) return;

    const newMessage = {
      content: currentMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    setCurrentMessage('');
    setIsThinking(true);

    try {
      const prompt = `Based on the following story/content:

${generatedContent}

User's request for changes: ${currentMessage}

Please provide an updated version of the content that incorporates these changes while maintaining the original style, tone, and format. Ensure the response is a complete, revised version of the content.`;

      const response = await geminiApi.generateText(prompt);

      const aiMessage = {
        content: response.content || response.message,
        sender: 'ai',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
      setGeneratedContent(response.content || response.message);
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('Failed to update content');
    } finally {
      setIsThinking(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-white relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0)_43%),radial-gradient(circle_at_-20%_-20%,rgba(167,243,208,0.08),rgba(255,255,255,0)_35%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,197,253,0.08),rgba(255,255,255,0)_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modern SaaS Header */}
        <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 shadow-2xl overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute -left-48 -top-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-500/40 via-primary-300/10 to-primary-400/40 blur-xl opacity-30"></div>
            </div>
            <div className="absolute -right-48 -bottom-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-400/40 via-primary-300/10 to-primary-500/40 blur-xl opacity-30"></div>
            </div>
          </div>

          {/* Content */}
          <div className="relative">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-800/50 border border-primary-700/50 backdrop-blur-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                  <span className="text-sm text-primary-100">AI-Powered Writing</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Story Weaver
                </h1>
                <p className="text-lg text-primary-200 max-w-xl">
                  Transform your ideas into engaging stories with AI-powered creativity
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="p-6">
            <div className="space-y-8">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Left column - Content Generation */}
                <div className="space-y-8">
                  <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="p-8">
                      <h2 className="text-2xl font-semibold text-gray-900 mb-8">Content Settings</h2>
                      <div className="space-y-8">
                        {/* Voice Recorder */}
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Record Audio
                          </label>
                          <VoiceRecorder 
                            onTranscriptionComplete={handleTranscriptionComplete}
                            placeholder="Click to start recording your content..."
                          />
                        </div>

                        {/* Content Input */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Overview
                          </label>
                          <textarea
                            value={contentInput}
                            onChange={(e) => setContentInput(e.target.value)}
                            rows="4"
                            placeholder="Record audio or type your content requirements..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        {/* Format and Style Settings */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Format Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Content Format
                            </label>
                            <div className="grid grid-cols-1 gap-2">
                              {formatOptions.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => setFormat(option.value)}
                                  className={`flex items-center justify-center p-3 rounded-lg border ${
                                    format === option.value
                                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                                      : 'border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Tone Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Communication Tone
                            </label>
                            <div className="grid grid-cols-1 gap-2">
                              {toneOptions.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => setTone(option.value)}
                                  className={`flex items-center justify-center p-3 rounded-lg border ${
                                    tone === option.value
                                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                                      : 'border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Additional Settings */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Target Audience */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Target Audience
                            </label>
                            <div className="grid grid-cols-1 gap-2">
                              {audienceOptions.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => setAudience(option.value)}
                                  className={`flex items-center justify-center p-3 rounded-lg border ${
                                    audience === option.value
                                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                                      : 'border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Content Length */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Content Length
                            </label>
                            <div className="grid grid-cols-1 gap-2">
                              {lengthOptions.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => setContentLength(option.value)}
                                  className={`flex items-center justify-center p-3 rounded-lg border ${
                                    contentLength === option.value
                                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                                      : 'border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={generateContent}
                          disabled={isGenerating}
                          className={`w-full px-6 py-4 rounded-lg font-medium text-white transition-all duration-200 ${
                            isGenerating
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-primary-600 hover:bg-primary-700'
                          }`}
                        >
                          {isGenerating ? (
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Generating Content...
                            </div>
                          ) : (
                            'Generate Content'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column - Generated Content and Chat */}
                <div className="space-y-8">
                  {/* Generated Content Display */}
                  {generatedContent && (
                    <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                      <div className="p-8">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Generated Content</h3>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => copyToClipboard(generatedContent)}
                              className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <span>Copy</span>
                            </button>
                          </div>
                        </div>
                        <div className="prose max-w-none">
                          <div className="whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border border-gray-200">{generatedContent}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chat Interface */}
                  {generatedContent && (
                    <div className="bg-white rounded-3xl shadow-lg overflow-hidden flex flex-col h-[600px]">
                      <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Refine Your Content</h3>
                        <p className="text-sm text-gray-500">Request changes or improvements to your generated content</p>
                      </div>

                      {/* Messages container */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((message, index) => (
                          <div
                            key={index}
                            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg p-3 ${
                                message.sender === 'user'
                                  ? 'bg-primary-600 text-white shadow-sm'
                                  : message.type === 'initial' 
                                    ? 'bg-gray-50 text-gray-900 border border-gray-200'
                                    : 'bg-gray-100 text-gray-900 shadow-sm'
                              }`}
                            >
                              {message.type === 'initial' && (
                                <div className="text-xs font-medium text-gray-500 mb-1">Initial Generation</div>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                              <span className="text-xs opacity-75 mt-1 block">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        ))}
                        {isThinking && (
                          <div className="flex justify-start">
                            <div className="bg-gray-100 rounded-lg p-3 flex items-center space-x-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Message input */}
                      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-gray-50">
                        <div className="flex space-x-4">
                          <input
                            type="text"
                            value={currentMessage}
                            onChange={(e) => setCurrentMessage(e.target.value)}
                            placeholder="Request changes or improvements..."
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                          />
                          <button
                            type="submit"
                            disabled={!currentMessage.trim() || isThinking}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                          >
                            Send
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <QuickStart guides={quickStartGuides} />
          <DeploymentOptions appId="creative-writer" position="none" />
        </div>
      </div>
    </div>
  );
}

export default StoryWeaver; 